// TMOS Setup Wizard - Organization Basics Step
// Date: December 19, 2025
// Purpose: Organization basic information step for tenant admin setup

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';

interface OrganizationBasicsStepProps {
  setupData: Record<string, any>;
  onComplete: (data: Record<string, any>) => void;
  onNext: () => void;
  loading: boolean;
}

export const OrganizationBasicsStep: React.FC<OrganizationBasicsStepProps> = ({
  setupData,
  onComplete,
  loading
}) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    companyName: setupData.companyName || '',
    domain: setupData.domain || '',
    industry: setupData.industry || '',
    organizationSize: setupData.organizationSize || '',
    primaryLocation: setupData.primaryLocation || '',
    timeZone: setupData.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: setupData.language || 'en-US'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete({
      organization_basics: formData,
      companyName: formData.companyName,
      domain: formData.domain,
      industry: formData.industry,
      organizationSize: formData.organizationSize,
      primaryLocation: formData.primaryLocation,
      timeZone: formData.timeZone,
      language: formData.language
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('setupWizard.organization.companyName') || 'Company Name'} *
          </label>
          <Input
            value={formData.companyName}
            onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
            required
            placeholder="Enter company name"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('setupWizard.organization.domain') || 'Domain'}
          </label>
          <Input
            value={formData.domain}
            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
            placeholder="example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('setupWizard.organization.industry') || 'Industry'}
          </label>
          <Select
            value={formData.industry}
            onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
            options={[
              { value: '', label: t('common.select') || 'Select...' },
              { value: 'technology', label: 'Technology' },
              { value: 'finance', label: 'Finance' },
              { value: 'healthcare', label: 'Healthcare' },
              { value: 'manufacturing', label: 'Manufacturing' },
              { value: 'retail', label: 'Retail' },
              { value: 'other', label: 'Other' }
            ]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('setupWizard.organization.size') || 'Organization Size'}
          </label>
          <Select
            value={formData.organizationSize}
            onChange={(e) => setFormData({ ...formData, organizationSize: e.target.value })}
            options={[
              { value: '', label: t('common.select') || 'Select...' },
              { value: '1-10', label: '1-10 employees' },
              { value: '11-50', label: '11-50 employees' },
              { value: '51-200', label: '51-200 employees' },
              { value: '201-1000', label: '201-1000 employees' },
              { value: '1000+', label: '1000+ employees' }
            ]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('setupWizard.organization.primaryLocation') || 'Primary Location'}
          </label>
          <Input
            value={formData.primaryLocation}
            onChange={(e) => setFormData({ ...formData, primaryLocation: e.target.value })}
            placeholder="City, Country"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('setupWizard.organization.timeZone') || 'Time Zone'}
          </label>
          <Input
            value={formData.timeZone}
            onChange={(e) => setFormData({ ...formData, timeZone: e.target.value })}
            placeholder="UTC"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={loading || !formData.companyName}>
          {t('common.continue') || 'Continue'}
        </Button>
      </div>
    </form>
  );
};

